<template>
  <div class="create-events">
    <div class="loading-screen" v-if="globalLoading">
      <LoadSpinner :color="`#${organizationDetails.darkBrandColor}`" />
    </div>
    <div class="events" v-else>
      <h1 v-translate>Add details</h1>
      <InfoBox class="info-box" :content="content" />
      <div class="event-container" v-for="event in events" :key="event.category">
        <EventComponent :event="event" @fieldBlur="saveFieldUpdate" />
      </div>
      <div v-bind:class="{ 'request-questions': currentRequest.requestQuestions.length }">
        <template v-if="currentRequest.requestQuestions.length">
          <template v-for="question in currentRequest.requestQuestions">
            <h5 class="request-question" :key="question.id">
              {{ $language.current | translateToCurrentLang(question.textEs, question.text) }}
            </h5>
            <div id="md-textarea">
              <textarea
                v-if="question.qType === 'FREE_TEXT'"
                class="grant-circles-textarea"
                rows="1"
                @input="mixin_TextAreaResize_resize"
                v-model="question.answer.text"
                :placeholder="placeholder"
                style="font-family: 'Poppins-Light'"
                @blur="saveFieldUpdate"
              >
              </textarea>
            </div>
            <md-field v-if="question.qType === 'MULTIPLE_CHOICE'">
              <md-select
                class="box"
                v-model="question.answer.text"
                @input="setSelected"
                :style="{ width: '100%' }"
              >
                <md-option
                  v-for="option in getSelectionOptions(question)"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </md-option>
              </md-select>
            </md-field>
            <md-field v-if="question.qType === 'NUMBER'">
              <md-input
                @blur="onBlur"
                v-model="question.answer.text"
                v-mask="$data[question.inputMask] || null"
                required
                type="text"
                :style="{ borderBottom: '1px solid gray' }"
                :placeholder="$gettext('Answer')"
              ></md-input>
            </md-field>
          </template>
        </template>
        <template v-if="!currentRequest.requestQuestions.length">
          <h5 v-translate>
            Please explain how the emergency (or emergencies) you selected created a financial
            hardship for you and your household? That is, how did they make it hard for you to
            afford basic living expenses?
          </h5>
          <div id="md-textarea">
            <textarea
              @input="mixin_TextAreaResize_resize"
              class="grant-circles-textarea"
              rows="1"
              v-model="currentRequest.eventsFinancialHardshipAnswer"
              :placeholder="placeholder"
              style="font-family: 'Poppins-Light'"
              @blur="saveFieldUpdate"
            />
          </div>
          <h5 style="margin-top: 2rem" v-translate>
            Was at least one of the emergencies described above unexpected or outside of your
            control?
          </h5>
          <div>
            <md-field>
              <md-select
                class="box"
                v-model="currentRequest.eventsUnexpectedOrUncontrollableMisc"
                :style="{ width: '100%' }"
              >
                <md-option
                  v-for="option in BOOLEAN_TEXT_CHOICES"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <h5 style="margin-top: 2rem" v-translate>
            Has the emergency or emergencies made it difficult for you to afford basic living
            expenses?
          </h5>
          <div>
            <md-field>
              <md-select
                class="box"
                v-model="currentRequest.eventsBasicLivingDifficultiesMisc"
                :style="{ width: '100%' }"
              >
                <md-option
                  v-for="option in BOOLEAN_TEXT_CHOICES"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <h5 style="margin-top: 2rem" v-translate>
            Is there anything else you’d like to share with us about your emergency or emergencies?
          </h5>
          <div id="md-textarea">
            <textarea
              class="grant-circles-textarea"
              @input="mixin_TextAreaResize_resize"
              rows="1"
              v-model="currentRequest.eventsMisc"
              :placeholder="placeholder"
              style="font-family: 'Poppins-Light'"
              @blur="saveFieldUpdate"
            />
          </div>
        </template>
      </div>

      <span class="text-error-msg" v-if="isEmptyEvent() || requiredAnswersMissing" v-translate>
        All questions are required to proceed except the final question. You can update your answers
        by editing the request later.
      </span>
      <md-button
        @click="onSubmit"
        class="button"
        :class="{ 'disabled-button': disabled }"
        :disabled="disabled"
      >
        <span v-show="organizationDetails.eventDocumentationEnabled && !loading" v-translate>Next: Emergency documentation</span>
        <span v-show="!organizationDetails.eventDocumentationEnabled && !loading" v-translate>Next: Describe Expenses</span>
        <span v-show="loading">
          <LoadSpinner color="white" />
        </span>
      </md-button>
    </div>
  </div>
</template>

<script>
import {Promise as bPromise} from "bluebird"
import { mapState } from 'vuex'
import EventComponent from '@/components/requests/EventComponent'
import InfoBox from '@/components/InfoBox'
import EventCategory from '@/services/eventCategories'
import Event from '@/services/events/'
import Request from '@/services/requests'
import LoadSpinner from '@/components/LoadSpinner'
import EventQuestion from '@/services/eventQuestions'
import TextAreaResize from '../../mixins/TextAreaResize'
import { analyticsMixin } from '@/services/googleAnalytics'
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
const currencyMask = createNumberMask({
  prefix: '$',
  allowDecimal: true,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
  allowNegative: false,
});

export default {
  name: 'CreateEvents',
  mixins: [analyticsMixin, TextAreaResize],
  components: {
    EventComponent,
    LoadSpinner,
    InfoBox,
  },
  data() {
    return {
      currencyMask,
      events: [],
      loading: false,
      globalLoading: true,
      content: this.$gettext('Please add details about each emergency.'),
      placeholder: this.$gettext('Answer'),
      BOOLEAN_TEXT_CHOICES: [
        {
          value: 'YES',
          label: this.$gettext('Yes'),
        },
        {
          value: 'NO',
          label: this.$gettext('No'),
        },
        {
          value: "I DON'T KNOW",
          label: this.$gettext("I don't know"),
        },
      ],
    }
  },
  async created() {
    this.globalLoading = true
    this.orderRequestQuestions()
    await this.assembleEvents()
    // this.startEditingPing()
    this.globalLoading = false
  },
  destroyed() {
    clearInterval(this.interval)
  },
  updated() {
  },
  methods: {
    getSelectionOptions(question) {
      if (question.friendlyName == 'Stimulus Check') {
        return this.BOOLEAN_TEXT_CHOICES
      }
      return this.BOOLEAN_TEXT_CHOICES.slice(0, 2)
    },
    async setSelected(value) {
      this.saveFieldUpdate()
    },
    async onSubmit() {
      this.loading = true
      this.trackClickedCTA()
      let updateFields = []

      if (this.organizationDetails.enableWhenItHappenedEventQuestion) {
        updateFields = ['dateOccurred', 'questions']
      } else {
        updateFields = ['questions']
      }

      const promiseList = this.events.map((event) => {
        // For each Event that has a populated ID, it already exists - update it
        if (event.id) {
          return Event.api.update(event, updateFields)
          // For each Event that does not, it does not yet exist - create it
        } else {
          return Event.api.create(event)
        }
      })
      // Wait for all Events to be created before updating Request
      await Promise.all(promiseList)

      updateFields = [
        'eventsFinancialHardshipAnswer',
        'eventsMisc',
        'eventsUnexpectedOrUncontrollableMisc',
        'eventsBasicLivingDifficultiesMisc',
        'requestQuestions',
      ]
      const response = await Request.api.update(this.currentRequest, updateFields)
      this.$store.dispatch('updateCurrentRequest', response)

      if (this.organizationDetails.eventDocumentationEnabled) {
        this.$router.push({ name: 'EventDocumentation' })
      } else {
        this.$router.push({ name: 'ExpenseCategories' })
      }

      this.loading = false
    },
    async saveFieldUpdate() {
      let updateFields = []

      if (this.organizationDetails.enableWhenItHappenedEventQuestion) {
        updateFields = ['dateOccurred', 'questions']
      } else {
        updateFields = ['questions']
      }

      const promiseList = this.events.map((event) => {
        // For each Event that has a populated ID, it already exists - update it
        if (event.id) {
          // Skip updating events that have incomplete fields
          let incompleteEvent = false
          event.questions.forEach((question) => {
            if (!question.answer.text) {
              incompleteEvent = true
            }
          })
          if (incompleteEvent) {
            return
          } else {
            return Event.api.update(event, updateFields)
          }
        }
      })
      await Promise.all(promiseList)

      updateFields = [
        'eventsFinancialHardshipAnswer',
        'eventsMisc',
        'eventsUnexpectedOrUncontrollableMisc',
        'eventsBasicLivingDifficultiesMisc',
        'requestQuestions',
      ]

      const response = await Request.api.update(this.currentRequest, updateFields)
      this.$store.dispatch('updateCurrentRequest', this.currentRequest)
    },

    orderRequestQuestions() {
      var last_item = this.currentRequest.requestQuestions.splice(
        this.currentRequest.requestQuestions.findIndex((question) => question.order == -1),
        1,
      )
      this.currentRequest.requestQuestions.sort((first, second) => first.order - second.order)
      this.currentRequest.requestQuestions = this.currentRequest.requestQuestions.concat(last_item)
    },
    /* Assemble an array of Event objects to populate this screen:
     *    - There will be an Event for each existing Event in the database.
     *    - There will be an new, empty Event for each new EventCategory
     *      selected on the previous screen.
     */
    async assembleEvents() {
      // Pull in any existing Events from state.
      // Cast them explicitly as Events, so we get access to their model methods.
      const events = this.currentRequest.eventsRef.map((e) => Event.create(e))

      // Find out which of our EventCategories already have an associated Event,
      // and which ones don't yet
      const categoriesWithEvent = events.map((event) => event.category)

      const categoriesWithoutEvent = []
      this.currentRequest.eventCategories.forEach((category) => {
        if (!categoriesWithEvent.includes(category)) {
          categoriesWithoutEvent.push(category)
        }
      })
      // For each category that doesn't have an associated Event, pull the right info
      // from the API, and create a local Event object
      const categoryInfo = []
      await bPromise.each( categoriesWithoutEvent, async(category) => {
        categoryInfo.push(await EventCategory.api.retrieve(category))
      })

      categoryInfo.forEach((cat) => {
        let event = Event.create({
          category: cat.id,
          categoryRef: cat,
          request: this.currentRequest.id,
          questions: cat.eventQuestions,
        })
        events.push(event)
      })
      // Render our combined list of new and existing Events
      this.events = events
    },
    /* Return true if one or more events are missing answers */
    isEmptyEvent() {
      let emptyEvent = false
      this.events.forEach((event) => {
        // COVID19 is a special event that doesn't need answers and should be skipped
        if (!event.isCovid19()) {
          if (this.organizationDetails.enableWhenItHappenedEventQuestion && !event.dateOccurred) {
            emptyEvent = true
          }
          event.questions.forEach((q) => {
            if (q.qType === EventQuestion.options.MULTIPLE_CHOICE) {
              if (!q.answer.option) {
                emptyEvent = true
              }
            } else {
              if (!q.answer.text) {
                emptyEvent = true
              }
            }
          })
        }
      })
      return emptyEvent
    },
    startEditingPing() {
      const request = this.currentRequest
      Request.api.editingPing(request)
      this.interval = setInterval(function () {
        Request.api.editingPing(request)
      }, 10000)
    },
  },
  computed: {
    ...mapState(['currentRequest', 'organizationDetails']),
    disabled() {
      return (
        this.globalLoading || this.loading || this.isEmptyEvent() || this.requiredAnswersMissing
      )
    },
    requiredAnswersMissing() {
      if (!this.currentRequest.requestQuestions.length) {
        return (
          !this.currentRequest.eventsFinancialHardshipAnswer ||
          !this.currentRequest.eventsUnexpectedOrUncontrollableMisc ||
          !this.currentRequest.eventsBasicLivingDifficultiesMisc
        )
      }

      return !this.currentRequest.requestQuestions.every((question) => {
        if (question.isOptional) {
          return true
        }
        if (question.answer != null && question.answer.text != null && question.answer.text != '') {
          return true
        }
        return false
      })
    },

    legacyRequestQuestionsCheck() {
      return (
        (this.currentRequest &&
          this.currentRequest.eventsFinancialHardshipAnswer &&
          this.currentRequest.eventsFinancialHardshipAnswer != '') ||
        (this.currentRequest.eventsMisc && this.currentRequest.eventsMisc != '') ||
        (this.currentRequest.eventsUnexpectedOrUncontrollableMisc &&
          this.currentRequest.eventsUnexpectedOrUncontrollableMisc != '') ||
        (this.currentRequest.eventsBasicLivingDifficultiesMisc &&
          this.currentRequest.eventsBasicLivingDifficultiesMisc != '')
      )
    },
  },
  watch: {
    'currentRequest.eventsBasicLivingDifficultiesMisc': async function () {
      await this.saveFieldUpdate()
    },
    'currentRequest.eventsUnexpectedOrUncontrollableMisc': async function () {
      await this.saveFieldUpdate()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
.loading-screen {
  margin: 3rem;
  height: 100vh;
}
.events {
  @include screen-container();
}
.info-box {
  margin: 1rem;
}
.button {
  @include primary-button(var(--dark-brand-color));
}
.request-questions {
  margin-top: -1.5rem;
}
.disabled-button {
  @include primary-button-disabled();
}
.event-container {
  width: 100%;
}

.grant-circles-textarea {
  overflow-y: scroll;
  width: 100%;
  box-sizing: border-box;
  border: none;
  outline: none;
  font-size: 1rem;
  resize: none;
  padding: 0.5rem 0.5rem 0.5rem 0;
  max-height: 100px;
  min-width: 15rem;
  appearance: none;
  border-bottom: 1px solid $gray;
}

.request-question {
  margin: 2rem 0 1rem 0;
}

#md-textarea {
  width: 100%;
}
</style>
